import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { replaceEmptyStringWithUndefined } from "@/shared/lib/object";
import {
  customZodResolver,
  useSubscribeFromValues,
} from "@/shared/lib/react-hook-form";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import { Box, Container, Stack, Typography } from "@mui/material";
import type React from "react";
import { useState } from "react";
import { type DeepPartial, useForm } from "react-hook-form";
import type { AdminCargoCreatePageProps } from "../../model";
import {
  type CreateOfferCargoFormSchemaType,
  createOfferCargoFormSchema,
} from "../../model/validation";
import { CargoForm } from "./forms/cargo-form";
import { CompanyForm } from "./forms/company-form";
import { DeliveryProposalForm } from "./forms/delivery-proposal-form";
import { DestinationsForm } from "./forms/destinations-form";
import { MemoForm } from "./forms/memo-form";
import { PaymentForm } from "./forms/payment-form";
import { ScheduleForm } from "./forms/schedule-form";
import { VehicleForm } from "./forms/vehicle-form";

export const CargoCreateInProgress: React.FC<AdminCargoCreatePageProps> = ({
  companies,
  defaultInputValues,
  onConfirmClick,
  onInputValuesUpdate,
}) => {
  const links = [
    { href: "/admin/cargos", label: "荷物一覧" },
    { label: "荷物の新規登録" },
  ];

  // FileオブジェクトをLocalStorageにシリアライズできないので、react-hook-formとは別に管理
  const [destinationFiles, setDestinationFiles] = useState<File[]>();

  const { handleSubmit, control, watch, getValues, formState } =
    useForm<CreateOfferCargoFormSchemaType>({
      resolver: customZodResolver(createOfferCargoFormSchema),
      mode: "onChange",
      defaultValues: defaultInputValues ?? {
        highwayFee: 1,
        destinations: [
          {
            hasLoading: true,
            hasUnloading: false,
            deadlineType: "till" as const,
            deadlineTime: undefined,
            deadlineTimeEnd: undefined,
            zipCode: undefined,
            prefecture: undefined,
            city: undefined,
            addressLine: undefined,
            buildingName: undefined,
            chargePersonName: undefined,
            contactInfo: undefined,
            taskDetail: undefined,
            memo: undefined,
          },
          {
            hasLoading: false,
            hasUnloading: true,
            deadlineType: "till" as const,
            deadlineTime: undefined,
            deadlineTimeEnd: undefined,
            zipCode: undefined,
            prefecture: undefined,
            city: undefined,
            addressLine: undefined,
            buildingName: undefined,
            chargePersonName: undefined,
            contactInfo: undefined,
            taskDetail: undefined,
            memo: undefined,
          },
        ],
      },
    });
  const handleInputValuesUpdate = (
    values: DeepPartial<CreateOfferCargoFormSchemaType>,
  ) => {
    onInputValuesUpdate(replaceEmptyStringWithUndefined(values));
  };
  useSubscribeFromValues(handleInputValuesUpdate, watch, true);

  const hasErrors = Object.keys(formState.errors).length > 0;

  // 詳細入力タブでのみ表示されるフィールドにバリデーションエラーがあるかチェック

  return (
    <Box px={{ xs: 2, sm: 4 }}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          荷物の新規登録
        </Typography>
      </Box>

      <Box
        sx={{
          mx: { xs: -2, sm: 0 },
        }}
      >
        <form
          onSubmit={handleSubmit((data) => {
            onConfirmClick({
              ...data,
              stage: "draft",
              destinationFiles,
            });
          })}
        >
          <Stack
            alignItems="stretch"
            bgcolor="grey.50"
            minHeight="600px"
            flexGrow={1}
            py={2}
          >
            <Container maxWidth="md" sx={{ px: { xs: 0, sm: 2 } }}>
              <Stack spacing={3} sx={{ mt: 4, mb: { xs: 4, sm: 10 }, py: 2 }}>
                <CompanyForm
                  control={control}
                  companies={
                    companies.status === "success" ? companies.data : null
                  }
                />
                <ScheduleForm control={control} />
                <DestinationsForm
                  control={control}
                  formState={formState}
                  watch={watch}
                  onDestinationFilesChange={setDestinationFiles} // FileオブジェクトをLocalStorageにシリアライズできないので、react-hook-formとは別に管理
                  onDestinationInsertOrRemove={() => {
                    // useFieldArrayのinsert/removeがwatchを発火させないため、手動で更新
                    handleInputValuesUpdate(getValues());
                  }}
                />
                <PaymentForm control={control} />
                <CargoForm control={control} />
                <VehicleForm control={control} />
                <MemoForm control={control} />
                <DeliveryProposalForm control={control} />
              </Stack>
            </Container>
          </Stack>
          <Stack spacing={2} p={2} alignItems="center" justifyContent="center">
            {hasErrors && (
              <BaseMessageCard
                status="error"
                message={
                  "正しく入力されていない項目があります。再度ご確認ください。"
                }
              />
            )}
            <BaseButton
              type="submit"
              variant="contained"
              color="primary"
              sx={{ my: 2, width: "240px" }}
            >
              確認する
            </BaseButton>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};
